/**
 * A folha do "ouvir junto" — o `listen_along_popup.dart`.
 *
 * Mora sozinha porque quem a abre são dois: o cartão da vitrola e cada linha
 * das estatísticas. No Flutter as duas telas chamam `showDialog` com o mesmo
 * `ListenAlongPopup`; aqui o equivalente é o mesmo componente importado nos
 * dois lugares.
 *
 * O que o navegador obrigou a mudar: a `Folha` da casa escreve o título antes
 * do conteúdo, e o `ListenAlongPopup` mostra a capa primeiro. A capa sobe para
 * cima do título pela ordem do grid, e não por uma segunda folha.
 *
 * A conta não vai no link, como diz o comentário de lá: o YouTube Music
 * descarta esse parâmetro. Quem escolhe a conta é o botão dos ajustes.
 *
 * O que o Flutter não tem: "Ouvir aqui". Lá a folha só sabia mandar para fora.
 * Quando existe uma gravação livre da mesma faixa no acervo, ela toca dentro
 * do app -- e o link do YouTube Music continua embaixo, porque na maioria das
 * faixas a gravação livre não existe e ele é a única saída.
 */

import { useEffect, useState } from 'react';
import {
  procurarGravacao,
  traduzir,
  type EstadoDeMusica,
  type Gravacao,
} from '@nosso-sempre/shared';
import { usarTocador } from '../nucleo/tocador';
import { Folha } from './Folha';
import { Icone } from './Icone';
import './ouvirJunto.css';

export function OuvirJunto({
  estado,
  aoFechar,
}: {
  estado: EstadoDeMusica | null;
  aoFechar: () => void;
}) {
  const tocador = usarTocador();
  const [gravacao, setGravacao] = useState<Gravacao | null>(null);
  const [procurando, setProcurando] = useState(false);

  const artista = estado?.artistName ?? '';
  const faixa = estado?.trackName ?? '';

  /**
   * Pergunta ao acervo só quando a folha abre, e não a cada volta do relógio
   * do Jukebox: o cartão se redesenha a cada 30 segundos, e a busca custa dois
   * pedidos. O `vivo` é contra a resposta lenta de uma faixa que já saiu de
   * cena -- sem ele, fechar e abrir noutra música mostraria a gravação da
   * anterior.
   *
   * A saída de cima também desliga o `procurando`, e não é enfeite: o único
   * lugar que o desligava era o `finally` lá embaixo, atrás do mesmo `vivo` que
   * a limpeza mata. Quem fechava a folha, ou recebia do Jukebox uma faixa sem
   * artista, no meio da busca deixava o recado "Procurando uma gravação
   * livre..." aceso para sempre, sobre uma música que ninguém estava
   * procurando. É o mesmo buraco da busca de livros (ver
   * `telas/livros/busca.tsx`).
   */
  useEffect(() => {
    if (!artista || !faixa) {
      setGravacao(null);
      setProcurando(false);
      return;
    }
    let vivo = true;
    setProcurando(true);
    setGravacao(null);
    void procurarGravacao(artista, faixa)
      .then((g) => {
        if (vivo) setGravacao(g);
      })
      .finally(() => {
        if (vivo) setProcurando(false);
      });
    return () => {
      vivo = false;
    };
  }, [artista, faixa]);

  function ouvirAqui() {
    if (!gravacao || !estado) return;
    tocador.tocar({
      gravacao,
      titulo: estado.trackName,
      artista: estado.artistName,
      /* O Last.fm deixa a capa em branco, e a barra desenha a nota no lugar. */
      capaUrl: estado.imageUrl ?? '',
    });
    aoFechar();
  }

  return (
    <Folha
      aberta={estado !== null}
      aoFechar={aoFechar}
      titulo={estado?.trackName ?? ''}
      centro
      classe="oj"
    >
      {estado ? (
        <>
          <span className="oj-capa">
            {estado.imageUrl ? (
              <img src={estado.imageUrl} alt="" width={200} height={200} />
            ) : (
              <span className="oj-sem-capa" aria-hidden="true">
                <Icone nome="nota-musical" tamanho={80} />
              </span>
            )}
          </span>
          <p className="oj-artista">{estado.artistName}</p>
          <p className="oj-album">{estado.albumName}</p>

          {gravacao ? (
            <>
              <button className="oj-botao" type="button" onClick={ouvirAqui}>
                <Icone nome="tocar-circulo" tamanho={24} />
                {traduzir('Ouvir aqui')}
              </button>
              {/* De onde a gravação saiu. Não é enfeite: é outra gravação da
                  mesma música, não a que a outra pessoa está ouvindo, e quem
                  ouve merece saber disso antes de estranhar. */}
              <p className="oj-fonte">
                {traduzir('Gravação livre do Internet Archive: {album}', {
                  album: gravacao.album,
                })}
              </p>
            </>
          ) : procurando ? (
            <p className="oj-fonte" role="status">
              {traduzir('Procurando uma gravação livre...')}
            </p>
          ) : null}

          <a
            className={gravacao ? 'oj-fora' : 'oj-botao'}
            href={estado.listenUrl}
            target="_blank"
            rel="noreferrer"
            onClick={aoFechar}
          >
            <Icone nome="tocar-circulo" tamanho={gravacao ? 18 : 24} />
            {traduzir('Ouvir no YouTube Music')}
          </a>
          <button className="oj-fechar" type="button" onClick={aoFechar}>
            {traduzir('Fechar')}
          </button>
        </>
      ) : null}
    </Folha>
  );
}
